/*
 * Sidebar link to the Attributions page, with a dot when something is waiting to be claimed.
 *
 * A dot, never a count — the same reasoning as the Notifications nav: a number is a second place
 * for a fact to be wrong and goes stale the moment somebody attributes one in another tab. The dot
 * is raised only when there is at least one item with a confident guess (something a person could
 * confirm in a click); the unguessable ones sit on the page but do not demand attention from the
 * nav. When it cannot ask — a failed fetch, a guest session — it shows nothing, because attestation
 * is optional and a nag for a courtesy would be worse than silence.
 */
import { useEffect, useState } from 'react';
import { SidebarItem } from '@backstage/core-components';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import AssignmentTurnedIn from '@material-ui/icons/AssignmentTurnedIn';

import { ControlPlaneClient } from '../fleet/controlPlane';
import { ATTRIBUTIONS_PATH } from './index';

interface PendingLike {
  readonly proposal: unknown;
}

export function AttributionsNavItem() {
  const config = useApi(configApiRef);
  const identity = useApi(identityApiRef);
  const [flag, setFlag] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controlPlaneUrl =
      config.getOptionalString('stackschematic.controlPlaneUrl') ?? window.location.origin;

    // Once, on mount — a signpost, not a live counter (see NotificationsNavItem for why the dot is
    // not polled).
    void (async () => {
      try {
        const { token } = await identity.getCredentials();
        const client = new ControlPlaneClient({ baseUrl: controlPlaneUrl, hostToken: token });
        const body = await client.call<{ pending: PendingLike[] }>('/admin/attributions/pending');
        // Flag only when there is a confident guess to confirm; unguessable items wait quietly.
        if (!cancelled) setFlag(body.pending.some(p => p.proposal !== null));
      } catch {
        if (!cancelled) setFlag(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config, identity]);

  return (
    <SidebarItem
      icon={AssignmentTurnedIn}
      to={ATTRIBUTIONS_PATH}
      text="Attributions"
      hasNotifications={flag}
    />
  );
}
